import { fn } from '@ember/helper';
import { not } from 'tio-ui/utilities';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import { RadioGroup } from 'tio-ui/components/forms';
import type EmployeeModel from 'tio-common/models/employee';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router-service';
import type SessoionContextService from 'tio-employee/services/session-context';
import type StoreService from '@ember-data/store';
import { Input } from 'tio-ui/components/forms';
import { Button } from 'tio-ui/components/buttons';
import { on } from '@ember/modifier';

export interface TuitionAssistanceFormsMentoringSignature {
  Args: {
    employee: EmployeeModel;
    isNonTa?: boolean;
  };
}

export default class TuitionAssistanceFormsMentoring extends Component<TuitionAssistanceFormsMentoringSignature> {
  @service declare sessionContext: SessoionContextService;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare store: StoreService;

  @tracked topic = null;
  @tracked previousCredentials = '';
  @tracked preferredFormat?: string;
  @tracked previousLoans?: string;
  @tracked educationType = '';
  @tracked currentlyEnrolled?: string;
  @tracked institutionCounselor?: string;
  @tracked preferredContactMethod?: string;
  @tracked submissionError = false;

  get employee() {
    return this.args.employee;
  }

  get plainText() {
    const employeeInfo = {
      firstName: this.employee.firstName,
      lastName: this.employee.lastName,
      id: this.employee.id,
      email: this.employee.email,
    };

    const answers = {
      scheduling_question_topic: this.topic,
      previous_relevant_credentials: this.previousCredentials,
      preferred_learning_format: this.preferredFormat,
      previous_loans: this.previousLoans,
      type_of_education: this.educationType,
      currently_enrolled: this.currentlyEnrolled,
      counselor_at_selected_institution: this.institutionCounselor,
      preferred_contact_method: this.preferredContactMethod,
      ...employeeInfo,
    };

    let plainText = '';

    Object.keys(answers).forEach((question) => {
      // @ts-expect-error: not sure about this one
      if (answers[question]) {
        // @ts-expect-error: not sure about this one
        plainText += ` ${question}: ${answers[question]}.` + '\n';
      }
    });
    return plainText;
  }

  get submitEnabled() {
    const fieldsPresent =
      this.topic &&
      this.previousCredentials &&
      this.preferredFormat &&
      this.previousLoans &&
      this.educationType &&
      this.currentlyEnrolled &&
      this.institutionCounselor &&
      this.preferredContactMethod;

    if (!fieldsPresent) {
      return false;
    } else {
      return true;
    }
  }

  get submitNonTaEnabled() {
    const fieldsPresent = this.topic && this.previousLoans && this.preferredContactMethod;

    if (!fieldsPresent) {
      return false;
    } else {
      return true;
    }
  }

  submitRequest = async () => {
    const adapter = this.store.adapterFor('application');
    const ok: boolean = await adapter.submitTasRequest(this.plainText);

    this.submissionError = !ok;
    if (!ok) return;

    // If phone is preferred, open Calendly in a new tab (Safari-friendly delay)
    if (this.preferredContactMethod === 'Phone') {
      setTimeout(() => {
        const newWindow = window.open(
          'https://calendly.com/ta-mentoring',
          '_blank',
          'noopener,noreferrer'
        );
        if (newWindow) {
          newWindow.opener = null;
        }
      }, 0);
    }

    // Navigate to TA dashboard and flag success via query param
    this.router.transitionTo('authenticated.tas.dashboard', {
      queryParams: { formSubmitted: true },
    });
  };

  submitNonTaRequest = async () => {
    const adapter = this.store.adapterFor('application');
    const ok: boolean = await adapter.submitNonTaRequest(this.plainText);

    this.submissionError = !ok;
    if (!ok) return;

    // Scheduling is only needed for a phone session
    if (this.preferredContactMethod === 'Phone') {
      this.router.transitionTo('authenticated.college-planning-mentors.index', {
        queryParams: { phoneScheduler: true },
      });
    } else {
      this.router.transitionTo('authenticated.dashboard', {
        queryParams: { formSubmitted: true },
      });
    }
  };

  <template>
    {{#if @isNonTa}}
      <div class="p-2 ml-4 max-w-2xl">
        <p class="py-2">{{t "tuition_assistance.mentoring.question_1"}}</p>
        {{! @glint-expect-error: need to research how to appease frontile }}
        <Input @onInput={{fn (mut this.topic)}} @size="lg" class="ml-3" />
      </div>
      <div class="p-2 ml-4">
        <p class="py-2">{{t "tuition_assistance.mentoring.question_2"}}</p>
        <RadioGroup
          class="ml-3"
          @value={{this.previousLoans}}
          @size="lg"
          @onChange={{fn (mut this.previousLoans)}}
          as |Radio|
        >
          <Radio @value="Private" @label="Private" />
          <Radio @value="Federal" @label="Federal" />
          <Radio @value="Both" @label="Both" />
          <Radio @value="I don't have student loans" @label="I don't have student loans" />
        </RadioGroup>
      </div>
      <div class="p-2 ml-4">
        <p class="py-2">{{t "tuition_assistance.mentoring.question_3"}}</p>
        <RadioGroup
          class="ml-3"
          @value={{this.preferredContactMethod}}
          @size="lg"
          @onChange={{fn (mut this.preferredContactMethod)}}
          as |Radio|
        >
          <Radio @value="Email" @label={{t "tuition_assistance.mentoring.send_me_an_email"}} />
          <Radio @value="Phone" @label={{t "tuition_assistance.mentoring.schedule_phone_call"}} />
        </RadioGroup>
      </div>
    {{else}}
      <div class="p-2 ml-4 max-w-2xl">
        <p class="py-2">{{t "tuition_assistance.mentoring.question_1"}}</p>
        {{! @glint-expect-error: need to research how to appease frontile }}
        <Input @onInput={{fn (mut this.topic)}} @size="lg" class="ml-3" />
      </div>
      <div class="p-2 ml-4 max-w-2xl">
        <p class="py-2">{{t "tuition_assistance.mentoring.question_2"}}</p>
        <Input @onInput={{fn (mut this.previousCredentials)}} @size="lg" class="ml-3" />
      </div>
      <div class="p-2 ml-4">
        <p class="py-2">{{t "tuition_assistance.mentoring.question_3"}}</p>
        <RadioGroup
          class="ml-3"
          @value={{this.preferredFormat}}
          @size="lg"
          @onChange={{fn (mut this.preferredFormat)}}
          as |Radio|
        >
          <Radio @value="Online" @label="Online" />
          <Radio @value="In Person" @label="In Person" />
          <Radio @value="Hybrid" @label="Hybrid" />
        </RadioGroup>
      </div>
      <div class="p-2 ml-4">
        <p class="py-2">{{t "tuition_assistance.mentoring.question_4"}}</p>
        <RadioGroup
          class="ml-3"
          @value={{this.previousLoans}}
          @size="lg"
          @onChange={{fn (mut this.previousLoans)}}
          as |Radio|
        >
          <Radio @value="Private" @label="Private" />
          <Radio @value="Federal" @label="Federal" />
          <Radio @value="Both" @label="Both" />
          <Radio @value="I don't have student loans" @label="I don't have student loans" />
        </RadioGroup>
      </div>
      <div class="p-2 ml-4 max-w-2xl">
        <p class="py-2">{{t "tuition_assistance.mentoring.question_5"}}</p>
        <Input @onInput={{fn (mut this.educationType)}} @size="lg" class="ml-3" />
      </div>
      <div class="p-2 ml-4">
        <p class="py-2">{{t "tuition_assistance.mentoring.question_6"}}</p>
        <RadioGroup
          class="ml-3"
          @value={{this.currentlyEnrolled}}
          @size="lg"
          @onChange={{fn (mut this.currentlyEnrolled)}}
          as |Radio|
        >
          <Radio @value="No" @label="No" />
          <Radio @value="Yes" @label="Yes" />
        </RadioGroup>
      </div>
      <div class="p-2 ml-4 max-w-2xl">
        <p class="py-2">{{t "tuition_assistance.mentoring.question_7"}}</p>
        <Input @onInput={{fn (mut this.institutionCounselor)}} @size="lg" class="ml-3" />
      </div>
      <div class="p-2 ml-4">
        <p class="py-2">{{t "tuition_assistance.mentoring.question_8"}}</p>
        <RadioGroup
          class="ml-3"
          @value={{this.preferredContactMethod}}
          @size="lg"
          @onChange={{fn (mut this.preferredContactMethod)}}
          as |Radio|
        >
          <Radio @value="Email" @label={{t "tuition_assistance.mentoring.send_me_an_email"}} />
          <Radio @value="Phone" @label={{t "tuition_assistance.mentoring.schedule_phone_call"}} />
        </RadioGroup>
      </div>
    {{/if}}
    <div class="flex justify-center py-10">
      {{#if @isNonTa}}
        <Button
          @intent="primary"
          {{on "click" this.submitNonTaRequest}}
          disabled={{not this.submitNonTaEnabled}}
        >
          {{t "college_planning_mentors.contact"}}
        </Button>
      {{else}}
        <Button
          @intent="primary"
          {{on "click" this.submitRequest}}
          disabled={{not this.submitEnabled}}
        >
          {{t "common.submit"}}
        </Button>
      {{/if}}
    </div>
  </template>
}
